import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import Snackbar from '@mui/material/Snackbar';
import axios from 'axios';
import { Fragment, useEffect, useRef, useState } from 'react';
import { Field, Form, FormSpy } from 'react-final-form';
import { useNavigate, useLocation } from 'react-router-dom';
import Typography from './Modules/Components/Typography';
import FormButton from './Modules/Form/FormButton';
import FormFeedback from './Modules/Form/FormFeedback';
import RFTextField from './Modules/Form/RFTextField';
import { emailV, passwordValidate, required } from './Modules/Form/validation';
import AppFooter from './Modules/Views/AppFooter';
import AppForm from './Modules/Views/AppForm';
import { AppNavBar } from './Modules/Views/AppNavBar';
import withRoot from './Modules/withRoot';
import { useDarkMode } from '../CoreApp/context/DarkModeContext';

// Redux
import { useDispatch, useSelector } from 'react-redux';
import { authState, setAuth } from '../../Redux/authSlice';

// Types
type User = {
  id: string;
  name?: string;
  email?: string;
};

type SettingsData = {
  darkMode: boolean | string;
  notificationsEnabled?: boolean | string;
};

const SignIn = () => {
  const [sent, setSent] = useState<boolean>(false);
  const [emailAddress, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);

  const hasFetchedSettings = useRef(false);
  const timerRef = useRef<number>();
  const navigate = useNavigate();
  const location = useLocation();
  const token = new URLSearchParams(location.search).get('token');
  const { refreshDarkMode, toggleDarkMode, darkMode } = useDarkMode();

  const dispatch = useDispatch();
  const auth = useSelector(authState);

  const validate = (values: { [key: string]: string }) => {
    const errors = required(['email', 'password'], values);

    if (!errors.email) {
      const emailError = emailV(values.email);
      setEmail(values.email);
      if (emailError) {
        errors.email = emailError;
      }
    }
    if (!errors.password) {
      const passwordError = passwordValidate(values.password);
      setPassword(values.password);
      if (passwordError) {
        errors.password = passwordError;
      }
    }

    return errors;
  };

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleSubmit = async (): Promise<void> => {
    setSent(true);
  
    try {
      const response = await axios.post('/api/login', {
        emailAddress,
        password,
      }, {
        withCredentials: true,
      });
  
      if (response.status === 200) {
        dispatch(setAuth(true));
  
        const userResponse = await axios.get<User>('https://demo.insollex.com/api/user', {
          withCredentials: true,
        });
  
        setUser(userResponse.data);
  
        if (!hasFetchedSettings.current) {
          hasFetchedSettings.current = true;
  
          const settingsRes = await axios.get<SettingsData>('https://demo.insollex.com/api/settings', {
            withCredentials: true,
          });
  
          const toBoolean = (v: boolean | string) => v === true || v === 'true';
  
          if (
            typeof settingsRes.data.darkMode === 'boolean' &&
            settingsRes.data.darkMode !== darkMode
          ) {
            toggleDarkMode(settingsRes.data.darkMode);
          }
        }
  
        refreshDarkMode?.();
  
        navigate(token ? `/invite-access/${token}` : '/CoreApp', { replace: true });
      }
    } catch (error: any) {
      let errorMessage = 'An error occurred during sign-in';
  
      if (error.response) {
        const { status, data } = error.response;
  
        if (status === 401) {
          if (data?.message?.toLowerCase().includes('password')) {
            errorMessage = "Password doesn't match.";
          } else if (data?.message?.toLowerCase().includes('email')) {
            errorMessage = 'Email not found.';
          } else {
            errorMessage = data.message;
          }
        } else {
          errorMessage = data?.message || error.message;
        }
      }
  
      setError(errorMessage);
      setSnackbarOpen(true);
      setSent(false);
    }
  };
  
    return (
        <Fragment>
            <AppNavBar />
            <AppForm>
                <Fragment>
                    <Typography
                        variant="h3"
                        gutterBottom
                        marked="center"
                        align="center"
                    >
                        Sign In
                    </Typography>
                    <Typography variant="body2" align="center">
                        {'Not a member yet? '}
                        <Link href="/signup" align="center" underline="always">
                            Sign Up here
                        </Link>
                    </Typography>
                </Fragment>
                <Form
                    onSubmit={handleSubmit}
                    subscription={{ submitting: true }}
                    validate={validate}
                >
                    {({ handleSubmit: handleSubmit2, submitting, errors }) => (
                        <Box
                            component="form"
                            onSubmit={handleSubmit2}
                            noValidate
                            sx={{ mt: 6 }}
                        >
                            <Field
                                autoComplete="email"
                                autoFocus
                                component={RFTextField}
                                disabled={submitting || sent}
                                fullWidth
                                label="Email"
                                margin="normal"
                                name="email"
                                required
                                size="large"
                            />
                            {errors?.email && (
                                <FormFeedback error>{errors.email}</FormFeedback>
                            )}

<Field
    fullWidth
    size="large"
    component={RFTextField}
    disabled={submitting || sent}
    required
    name="password"
    autoComplete="current-password"
    label="Password"
    type={showPassword ? 'text' : 'password'}
    margin="normal"
    InputProps={{
        endAdornment: (
            <InputAdornment position="end">
                <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    sx={{
                        opacity: 0.6,  // Make the icon slightly transparent
                        padding: 0,    // Remove extra padding so it sits inside
                        position: 'relative',
                    }}
                >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
            </InputAdornment>
        ),
    }}
    sx={{
        '& .MuiOutlinedInput-root': {
            position: 'relative',
            pr: '14px', // Ensures space for the icon
        },
        '& .MuiInputAdornment-root': {
            position: 'absolute',
            right: 35, // Moves icon inside the text field
        },
    }}
/>


                            {errors?.password && (
                                <FormFeedback error>{errors.password}</FormFeedback>
                            )}

                            <FormSpy subscription={{ submitError: true }}>
                                {({ submitError }) =>
                                    submitError ? (
                                        <FormFeedback error sx={{ mt: 2 }}>
                                            {submitError}
                                        </FormFeedback>
                                    ) : null
                                }
                            </FormSpy>

                            <FormButton
                                sx={{ mt: 3, mb: 2 }}
                                disabled={submitting || sent}
                                size="large"
                                color="secondary"
                                fullWidth
                            >
                                {submitting || sent ? 'Validation in progress…' : 'Sign In'}
                            </FormButton>
                        </Box>
                    )}
                </Form>
                <Typography align="center">
                    <Link underline="always" href="/forgotpassword/">
                        Forgot password?
                    </Link>
                </Typography>
            </AppForm>
            <AppFooter />

            {/* Snackbar for error messages */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
            <Alert
  onClose={handleSnackbarClose}
  severity="error"
  sx={{
    backgroundColor: '#f8d7da',
    color: 'red',
    padding: '15px 25px',
    borderRadius: '5px',
    fontWeight: 'bold',
    fontSize: '14px',
    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
    border: '1px solid #f5c6cb',
    marginTop: '265px', 
  }}
>
  {error}
</Alert>
            </Snackbar>
        </Fragment>
    );
};

export default withRoot(SignIn);

